import { z } from 'zod';

/**
 * Documento (CPF ou CNPJ) — armazenado como **apenas dígitos** (SPEC.md §4).
 * Normalização e validação de dígito verificador ficam aqui, uma vez.
 */

export type TipoDocumento = 'cpf' | 'cnpj';

export class DocumentoInvalidoError extends Error {
  readonly codigo = 'documento_invalido' as const;
  constructor(readonly valorOriginal: string) {
    super(`Documento inválido: ${JSON.stringify(valorOriginal)}`);
    this.name = 'DocumentoInvalidoError';
  }
}

export function somenteDigitos(entrada: string): string {
  return entrada.replace(/\D/g, '');
}

function digitosIguais(s: string): boolean {
  return /^(\d)\1+$/.test(s);
}

function validarCpf(cpf: string): boolean {
  if (cpf.length !== 11 || digitosIguais(cpf)) return false;
  const calc = (fatorInicial: number): number => {
    let soma = 0;
    for (let i = 0; i < fatorInicial - 1; i++) {
      soma += Number(cpf[i]) * (fatorInicial - i);
    }
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return calc(10) === Number(cpf[9]) && calc(11) === Number(cpf[10]);
}

function validarCnpj(cnpj: string): boolean {
  if (cnpj.length !== 14 || digitosIguais(cnpj)) return false;
  const calc = (tamanho: number): number => {
    const pesos =
      tamanho === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let soma = 0;
    for (let i = 0; i < tamanho; i++) {
      soma += Number(cnpj[i]) * pesos[i]!;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return calc(12) === Number(cnpj[12]) && calc(13) === Number(cnpj[13]);
}

export function tipoDocumento(digitos: string): TipoDocumento | null {
  if (digitos.length === 11) return 'cpf';
  if (digitos.length === 14) return 'cnpj';
  return null;
}

export function documentoValido(digitos: string): boolean {
  const tipo = tipoDocumento(digitos);
  if (tipo === 'cpf') return validarCpf(digitos);
  if (tipo === 'cnpj') return validarCnpj(digitos);
  return false;
}

/** Normaliza para dígitos e valida o DV. Lança em caso inválido. */
export function normalizarDocumento(entrada: string): string {
  const digitos = somenteDigitos(entrada);
  if (!documentoValido(digitos)) {
    throw new DocumentoInvalidoError(entrada);
  }
  return digitos;
}

/** Zod: string livre -> dígitos validados. Opcional/nullable é decisão de cada endpoint. */
export const documentoSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    try {
      return normalizarDocumento(v);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Documento inválido' });
      return z.NEVER;
    }
  });
